import type { WizardFormState } from '../wizard/wizardState';
import type { WizardPathId } from '../wizard/wizardTypes';

const apiBaseUrl = 'https://d2mv59pjtqdwc1.cloudfront.net';
const registrationUrl = `${apiBaseUrl}/register`;
const groupAvailabilityUrl = registrationUrl;

export interface AvailableGroup {
  name: string;
  capacity: number;
  ages: string;
  currentStudents: number;
}

interface GroupAvailabilityResponse {
  success: boolean;
  error?: string;
  message?: string;
  data?: {
    groups?: AvailableGroup[];
  };
}

export interface RegistrationResponse {
  success: boolean;
  parentId?: string;
  volunteerIds?: string[];
  studentIds?: string[];
  error?: string;
  details?: string;
}

async function fetchGroupAvailability(): Promise<AvailableGroup[]> {
  const response = await fetch(groupAvailabilityUrl);

  if (!response.ok) {
    throw new Error(`Failed to load groups: ${response.status}`);
  }

  const data = (await response.json()) as GroupAvailabilityResponse;

  if (!data.success) {
    throw new Error(data.error || data.message || 'Failed to load groups');
  }

  return data.data?.groups ?? [];
}

async function submitRegistration(
  formState: WizardFormState,
  pathId: WizardPathId
): Promise<RegistrationResponse> {
  let registrationType: 'parent' | 'volunteer' | 'volunteerAndChild';
  
  // Convert allergies string to array
  const childrenWithAllergiesArray = formState.children.map(({ requestedGroup, ...child }) => ({
    ...child,
    requested_group: requestedGroup,
    allergies: child.allergies
      .split(',')
      .map(a => a.trim())
      .filter(a => a)
  }));
  
  const payload: any = {
    children: childrenWithAllergiesArray,
    emergencyContacts: formState.emergencyContacts
  };

  if (pathId === 'parentChild') {
    registrationType = 'parent';
    payload.parentInfo = formState.parentInfo;
  } else if (pathId === 'volunteerOnly') {
    registrationType = 'volunteer';
    payload.volunteers = formState.volunteers;
  } else if (pathId === 'volunteerAndChild') {
    registrationType = 'volunteerAndChild';
    payload.volunteers = formState.volunteers;
  } else {
    throw new Error(`Unknown registration type: ${pathId}`);
  }

  payload.registrationType = registrationType;

  try {
    const response = await fetch(registrationUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success) {
      return {
        success: true,
        parentId: data.data?.parentId,
        volunteerIds: data.data?.volunteerIds,
        studentIds: data.data?.studentIds,
      };
    } else {
      return {
        success: false,
        error: data.error,
        details: data.details,
      };
    }
  } catch (error) {
    console.error('Registration submission error:', error);
    return {
      success: false,
      error: 'Failed to submit registration',
      details: String(error),
    };
  }
}

export { fetchGroupAvailability, submitRegistration };
